import {useEffect, useMemo, useState} from 'react'
import {fetchQuery, graphql, useRelayEnvironment, useSubscription} from 'react-relay'

import type {useActivityTimelineCountQuery} from './__generated__/useActivityTimelineCountQuery.graphql'
import type {
  useActivityTimelineCountSubscription,
  useActivityTimelineCountSubscription$data,
} from './__generated__/useActivityTimelineCountSubscription.graphql'

/**
 * Lightweight query that fetches only the total count of ACTIVITY timeline items.
 * Loaded as a tertiary query so it doesn't block the main page render.
 */
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const activityCountQuery = graphql`
  query useActivityTimelineCountQuery($issueId: ID!) {
    node(id: $issueId) {
      ... on Issue {
        timelineItems(first: 0, viewType: ACTIVITY) {
          totalCount
        }
      }
    }
  }
`

/**
 * Subscription to keep the activity count live. Fires whenever the issue is
 * updated and re-reads the ACTIVITY totalCount from the server.
 */
// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
const activityCountSubscription = graphql`
  subscription useActivityTimelineCountSubscription($issueId: ID!) {
    issueUpdated(id: $issueId) {
      issueTimelineUpdated {
        timelineItems(first: 0, viewType: ACTIVITY) {
          totalCount
        }
      }
    }
  }
`

/**
 * Returns the total count of ACTIVITY timeline items for the given issue.
 *
 * Fires a lightweight tertiary query on mount and subscribes for real-time
 * updates, keeping the Activity tab badge in sync without blocking the main
 * page load or slowing down the conversation timeline.
 */
export function useActivityTimelineCount(issueId: string): number | undefined {
  const environment = useRelayEnvironment()
  const [count, setCount] = useState<number | undefined>(undefined)

  // Fire the tertiary query after mount
  useEffect(() => {
    const sub = fetchQuery<useActivityTimelineCountQuery>(environment, activityCountQuery, {issueId}).subscribe({
      next: data => {
        const totalCount = data.node?.timelineItems?.totalCount
        if (totalCount != null) {
          setCount(totalCount)
        }
      },
    })
    return () => sub.unsubscribe()
  }, [environment, issueId])

  // Subscribe for live updates to the ACTIVITY count
  const subscriptionConfig = useMemo(
    () => ({
      subscription: activityCountSubscription,
      variables: {issueId},
      onNext: (resp: useActivityTimelineCountSubscription$data | null | undefined) => {
        const totalCount = resp?.issueUpdated?.issueTimelineUpdated?.timelineItems?.totalCount
        if (totalCount != null) {
          setCount(totalCount)
        }
      },
    }),
    [issueId],
  )

  useSubscription<useActivityTimelineCountSubscription>(subscriptionConfig)

  return count
}
